import { recordStudyVisit, recordEmailCampaignReturn } from "./study-conversion.functions";
import { isEmailReactivation } from "./email-reactivation-policy";

/**
 * Privacy-safe first-party campaign attribution. Only standard UTM campaign
 * parameters, the landing path and a coarse device class are captured — never
 * click IDs, emails, homework content or other personal data. Values are
 * stored in sessionStorage so a tagged ad click can be tied to a confirmed
 * registration without any third-party tracker.
 */
export interface FunnelAttribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  landingPage?: string;
  deviceType?: string;
}

const ATTR_KEY = "inquira.study.funnel.attr";
const UTM_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_\- .]{0,99}$/;

function cleanUtm(value: string | null): string | undefined {
  if (!value) return undefined;
  const v = value.trim();
  if (v.length > 100) return undefined;
  return UTM_PATTERN.test(v) ? v : undefined;
}

function coarseDevice(ua: string): string {
  if (/iPad|Tablet/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return "tablet";
  if (/Mobile|iPhone|Android/i.test(ua)) return "phone";
  return "desktop";
}

/** Pure parser — kept separate from browser globals so it is unit-testable. */
export function parseFunnelAttribution(search: string, path: string, userAgent: string): FunnelAttribution {
  const params = new URLSearchParams(search);
  const out: FunnelAttribution = {};
  const source = cleanUtm(params.get("utm_source"));
  const medium = cleanUtm(params.get("utm_medium"));
  const campaign = cleanUtm(params.get("utm_campaign"));
  if (source) out.utmSource = source.toLowerCase();
  if (medium) out.utmMedium = medium.toLowerCase();
  if (campaign) out.utmCampaign = campaign;
  if (path.startsWith("/") && !path.startsWith("//")) out.landingPage = path.slice(0, 200);
  out.deviceType = coarseDevice(userAgent);
  return out;
}

/** Captures attribution from the current URL once per browser session. */
export function captureFunnelAttribution(): FunnelAttribution {
  if (typeof window === "undefined") return {};
  try {
    const existing = window.sessionStorage.getItem(ATTR_KEY);
    const parsed = parseFunnelAttribution(window.location.search, window.location.pathname, navigator.userAgent);
    if (existing) {
      const previous = JSON.parse(existing) as FunnelAttribution;
      if (!parsed.utmCampaign || (previous.utmCampaign === parsed.utmCampaign && previous.utmSource === parsed.utmSource && previous.utmMedium === parsed.utmMedium)) return previous;
      // A newly tagged campaign is a new visit, even in an already-open session.
      window.sessionStorage.removeItem("inquira.study.funnel.session");
    }
    window.sessionStorage.setItem(ATTR_KEY, JSON.stringify(parsed));
    return parsed;
  } catch {
    return {};
  }
}

/** Attribution captured earlier this session (empty object when none). */
export function getFunnelAttribution(): FunnelAttribution {
  if (typeof window === "undefined") return {};
  try {
    const existing = window.sessionStorage.getItem(ATTR_KEY);
    return existing ? (JSON.parse(existing) as FunnelAttribution) : {};
  } catch {
    return {};
  }
}

export function studyFunnelSignal(event: "landing_visit" | "signup_cta" | "registration_started" | "signup_attempt") {
  if (typeof window === "undefined") return;
  try {
    let sessionId = sessionStorage.getItem("inquira.study.funnel.session");
    if (!sessionId) { sessionId = crypto.randomUUID(); sessionStorage.setItem("inquira.study.funnel.session", sessionId); }
    const attribution = captureFunnelAttribution();
    void recordStudyVisit({ data: { eventId: crypto.randomUUID(), sessionId, event, attribution } }).catch(() => {});
  } catch { /* Analytics never block studying or sign-in. */ }
}

/** Works for users already signed in and for users signing in after a tagged visit. */
export async function trackEmailCampaignReturn(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const attribution = captureFunnelAttribution();
    if (!isEmailReactivation(attribution)) return;
    let sessionId = sessionStorage.getItem("inquira.study.funnel.session");
    if (!sessionId) { sessionId = crypto.randomUUID(); sessionStorage.setItem("inquira.study.funnel.session", sessionId); }
    // Persist the anonymous touch before associating it with the validated account.
    const result = await recordStudyVisit({ data: { eventId: crypto.randomUUID(), sessionId, event: "landing_visit", attribution } });
    if (result.ok) await recordEmailCampaignReturn({ data: { sessionId } });
  } catch { /* Optional reporting never interrupts sign-in or learning. */ }
}
